/**
 * The one short sentence of a value proposition that is SPOKEN in the opening pitch.
 *
 * Shared by the server (which trims the value proposition before it goes into the flow) and the customize
 * page (whose preview must show what the agent will actually say, not the 100-150 words that sit behind it).
 */
export const SPOKEN_PITCH_BUDGET_WORDS = 12;

/**
 * The one sentence of the value proposition that gets SPOKEN.
 *
 * The field now runs to 100-150 words, because that depth is what makes the rest
 * of the script specific. But {{value_proposition}} is substituted verbatim into
 * "…helps {{target_audience}} {{value_proposition}}. Is that something you're
 * dealing with right now?" - so dropping 150 words in there buys a minute of
 * monologue before the prospect is asked anything.
 *
 * The prompt asks for a short standalone opening sentence precisely so this can
 * take it. If what comes back has no sentence break, it is cut on a word
 * boundary rather than mid-word.
 */
/**
 * Words a spoken clause must never end on.
 *
 * Cutting at exactly N words landed on "...without hiring more people to", which
 * the template then punctuated into "people to....". Said aloud that is the
 * agent trailing off mid-thought in the one line that has to land.
 */
const DANGLING = new Set([
  'a', 'an', 'the', 'to', 'of', 'and', 'or', 'for', 'with', 'without', 'in', 'on',
  'at', 'by', 'from', 'that', 'than', 'as', 'is', 'are', 'be', 'your', 'our', 'their',
]);

export function spokenValueProposition(text: string, maxWords = SPOKEN_PITCH_BUDGET_WORDS): string {
  const full = String(text || '').replace(/\s+/g, ' ').trim();
  if (!full) return '';

  const firstSentence = full.match(/^[^.!?]+[.!?]?/)?.[0]?.trim() ?? full;
  const candidate = firstSentence.split(' ').length <= maxWords ? firstSentence : full;

  let words = candidate.split(' ');
  if (words.length > maxWords) {
    words = words.slice(0, maxWords);
    // Prefer a clause boundary inside the budget over a hard cut: "...needs a
    // call," reads as a finished thought, "...more people to" does not.
    // Hand-rolled rather than findLastIndex: this project targets ES2020, where
    // that method does not exist.
    let lastComma = -1;
    for (let index = words.length - 1; index >= 0; index--) {
      if (/[,;:]$/.test(words[index])) { lastComma = index; break; }
    }
    if (lastComma >= Math.floor(maxWords / 2)) words = words.slice(0, lastComma + 1);
    while (words.length > 1 && DANGLING.has(words[words.length - 1].toLowerCase())) words.pop();
  }

  let spoken = words.join(' ').replace(/[\s,;:.]+$/, '');

  // Substituted mid-sentence ("...helps lending teams {{value_proposition}}"), so
  // a leading capital reads as a broken sentence. Only a plainly capitalised
  // word is lowered - VoicePro, NBFC and other names keep their shape.
  if (/^[A-Z][a-z]+$/.test(words[0] ?? '')) {
    spoken = spoken.charAt(0).toLowerCase() + spoken.slice(1);
  }

  return spoken;
}
